// ── Remote terminal binding (TPT495) ──
//
// Connects one xterm instance to one relay attachment. xterm only ever receives terminal data
// through term.write(): it parses escape sequences into a cell grid and paints text nodes, so
// nothing a device sends can become markup or script.
//
// The module imports nothing and takes the terminal, the fit addon and the relay client as
// parameters, so it runs under node:test. `reduceTerminalState()` and `terminalStatus()` are pure.

export const RESYNC_MIN_INTERVAL_MS = 5000;
export const REPAINT_SETTLE_MS = 150;
export const REPAINT_NUDGE_MS = 120;
export const RESIZE_DEBOUNCE_MS = 100;
export const DOUBLE_ESCAPE_MS = 400;

// The address an OSC 8 link in terminal data may open, or null. Terminal data comes from a process
// on a device, so a `javascript:` or `data:` link must never reach a browser window.
export function safeTerminalLink(uri) {
  try {
    const url = new URL(uri);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

export function initialTerminalState(session, connection) {
  return {
    connection,
    attached: false,
    live: false,
    readOnly: true,
    holder: null,
    sessionState: session?.state ?? 'running',
    exitCode: null,
    ended: null, // exited | lost | permission | revoked | refused
    code: null,
    gap: false,
  };
}

const ENDED_SESSION_STATES = { exited: 'exited', lost: 'lost', revoked: 'revoked' };
const ENDED_DETACH_REASONS = { session_exited: 'exited', session_lost: 'lost', permission_lost: 'permission' };

export function reduceTerminalState(state, event) {
  switch (event.type) {
    case 'connection': {
      if (event.connection === 'open') return { ...state, connection: 'open' };
      return { ...state, connection: event.connection, attached: false, live: false, holder: null };
    }
    case 'attached':
      return { ...state, attached: true, readOnly: event.readOnly, holder: event.holder };
    case 'attach-ok':
      return { ...state, live: true, gap: false };
    case 'controller':
      return { ...state, holder: event.holder };
    case 'gap':
      return { ...state, gap: true };
    case 'session': {
      const ended = ENDED_SESSION_STATES[event.session.state] ?? null;
      return {
        ...state,
        sessionState: event.session.state,
        exitCode: event.session.exit_code ?? state.exitCode,
        ended: state.ended ?? ended,
        live: ended ? false : state.live,
      };
    }
    case 'detached':
      return { ...state, attached: false, live: false, ended: state.ended ?? ENDED_DETACH_REASONS[event.reason] ?? 'lost' };
    case 'refused':
      return { ...state, attached: false, live: false, ended: state.ended ?? 'refused', code: event.code };
    default:
      return state;
  }
}

// Input reaches the PTY only from the attachment holding control, and only while its device is
// there: the device silently drops keys from anyone else, which would read as a frozen terminal.
export function terminalCanType(state) {
  return state.attached && state.live && !state.readOnly && state.holder === 'self'
    && !state.ended && state.connection === 'open' && state.sessionState === 'running';
}

// Stop needs the device and the link, not input control: a viewer can end a run Task App shows.
export function terminalCanStop(state) {
  return state.connection === 'open' && state.sessionState !== 'disconnected' && !state.ended;
}

function terminalRole(state) {
  if (state.ended || !state.attached || state.sessionState !== 'running') return null;
  if (state.readOnly) return 'roleReadOnly';
  return { self: 'roleControl', local: 'roleLocal', other: 'roleOther' }[state.holder] ?? 'roleNone';
}

// What the dialog shows for a state: `tone` colours the status dot, `labelKey` names it, `roleKey`
// says who holds input, `noticeKey` (with `noticeParams`) is the persistent banner. `retry` offers
// a button where the client will not recover by itself.
export function terminalStatus(state) {
  const status = { tone: 'wait', labelKey: 'connecting', roleKey: terminalRole(state), noticeKey: null, noticeParams: {}, retry: false, code: null };
  if (state.ended === 'exited') {
    return {
      ...status,
      tone: 'down',
      labelKey: 'sessionEnded',
      noticeKey: Number.isInteger(state.exitCode) ? 'noticeExitedCode' : 'noticeExited',
      noticeParams: { code: state.exitCode },
    };
  }
  if (state.ended === 'permission' || state.ended === 'revoked') {
    return { ...status, tone: 'down', labelKey: 'sessionEnded', noticeKey: 'noticePermissionLost' };
  }
  if (state.ended === 'lost') {
    return { ...status, tone: 'down', labelKey: 'sessionEnded', noticeKey: 'noticeDeviceLost' };
  }
  if (state.ended === 'refused') {
    return { ...status, tone: 'down', labelKey: 'sessionEnded', code: state.code };
  }
  if (state.connection === 'auth') return { ...status, tone: 'down', labelKey: 'disconnected', noticeKey: 'noticeSignIn' };
  if (state.connection === 'stopped') {
    return { ...status, tone: 'down', labelKey: 'disconnected', noticeKey: 'noticeRelayStopped', retry: true };
  }
  if (state.connection === 'reconnecting') return { ...status, labelKey: 'reconnecting', noticeKey: 'noticeReconnecting' };
  if (state.connection !== 'open' || !state.live) return status;
  if (state.sessionState === 'disconnected') {
    return { ...status, tone: 'down', labelKey: 'stateOffline', noticeKey: 'noticeDeviceOffline' };
  }
  return { ...status, tone: 'live', labelKey: 'connected', noticeKey: state.gap ? 'noticeGap' : null };
}

export function bindRemoteTerminal({ client, sessionId, session, term, fit, onChange, onClose, onInputRefused, setTimer, clearTimer, now }) {
  const schedule = setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const cancel = clearTimer ?? (id => clearTimeout(id));
  const clock = now ?? Date.now;

  let state = initialTerminalState(session, client.connection);
  let disposed = false;
  let replayWrites = 0;
  let lastSize = null;
  let lastEscapeAt = 0;
  let lastResyncAt = -Infinity;
  let settleTimer = null;
  let nudgeTimer = null;
  let resizeTimer = null;
  let resyncTimer = null;

  function sendSize(force) {
    const { cols, rows } = term;
    if (!force && lastSize?.cols === cols && lastSize?.rows === rows) return;
    lastSize = { cols, rows };
    attachment.resize(cols, rows);
  }

  // A full-screen TUI redraws on SIGWINCH, and a resize to the size it already has raises none:
  // one column down and back repaints the agent's screen after a bounded replay, whose start may
  // lie anywhere in its output, without sending it a key.
  function repaint() {
    settleTimer = null;
    if (!terminalCanType(state) || replayWrites > 0) return;
    if (term.cols < 3) {
      sendSize(true);
      return;
    }
    attachment.resize(term.cols - 1, term.rows);
    lastSize = null;
    nudgeTimer = schedule(() => {
      nudgeTimer = null;
      if (terminalCanType(state)) sendSize(true);
    }, REPAINT_NUDGE_MS);
  }

  function scheduleRepaint() {
    if (settleTimer !== null) cancel(settleTimer);
    settleTimer = schedule(repaint, REPAINT_SETTLE_MS);
  }

  function resync() {
    resyncTimer = null;
    lastResyncAt = clock();
    attachment.resync();
  }

  // Output was dropped, so the screen is wrong until a fresh replay. Bounded: every resync costs
  // one replay, and a device that keeps overrunning its budget would otherwise repeat them.
  function requestResync() {
    if (resyncTimer !== null) return;
    resyncTimer = schedule(resync, Math.max(0, lastResyncAt + RESYNC_MIN_INTERVAL_MS - clock()));
  }

  function update(event) {
    const could = terminalCanType(state);
    state = reduceTerminalState(state, event);
    const can = terminalCanType(state);
    if (can !== could) {
      term.options.disableStdin = !can;
      term.options.cursorBlink = can;
      if (can) scheduleRepaint();
    }
    onChange(state);
  }

  function write(data, replay) {
    if (!replay) {
      term.write(data);
      return;
    }
    replayWrites += 1;
    term.write(data, () => {
      replayWrites = Math.max(0, replayWrites - 1);
      if (replayWrites === 0 && !disposed) scheduleRepaint();
    });
  }

  function onEvent(event) {
    if (disposed) return;
    switch (event.type) {
      case 'output':
        write(event.data, event.replay);
        break;
      case 'reset':
        term.reset();
        break;
      case 'gap':
        update(event);
        requestResync();
        break;
      case 'attach-ok':
        lastSize = null;
        update(event);
        break;
      case 'input-refused':
        onInputRefused?.(event.code);
        break;
      default:
        update(event);
    }
  }

  term.options.disableStdin = true;
  term.options.cursorBlink = false;
  const attachment = client.attach(sessionId, onEvent);

  const dataListener = term.onData((data) => {
    // A replay holds the terminal queries of past output (cursor position, device attributes), and
    // xterm answers each one as if typed. Those answers must not reach the live agent.
    if (replayWrites > 0 || !terminalCanType(state)) return;
    attachment.input(data);
  });

  // Tab and a single Esc belong to the agent, so Esc twice is the way out of the terminal, as in
  // the Task App. Without input there is nothing to send and one is enough. The event must not
  // bubble on to the document, where the modal and the drawer listen for Escape.
  term.attachCustomKeyEventHandler((event) => {
    if (event.type !== 'keydown' || event.key !== 'Escape') return true;
    const at = clock();
    if (terminalCanType(state) && at - lastEscapeAt >= DOUBLE_ESCAPE_MS) {
      lastEscapeAt = at;
      return true;
    }
    lastEscapeAt = 0;
    event.preventDefault();
    event.stopPropagation();
    onClose();
    return false;
  });

  return {
    get state() { return state; },
    // Refits to the host. The PTY follows only while this attachment holds control: a viewer's
    // window must not reflow a terminal someone else is using.
    requestFit() {
      if (disposed || resizeTimer !== null) return;
      resizeTimer = schedule(() => {
        resizeTimer = null;
        if (disposed) return;
        fit.fit();
        if (terminalCanType(state)) sendSize(false);
      }, RESIZE_DEBOUNCE_MS);
    },
    // Stops the stream and leaves the session running.
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const timer of [settleTimer, nudgeTimer, resizeTimer, resyncTimer]) if (timer !== null) cancel(timer);
      dataListener.dispose();
      attachment.detach();
    },
  };
}
